import React, { useState } from 'react';
import { Box, Button, Typography, useMediaQuery, useTheme } from '@mui/material';
import { signOut } from 'firebase/auth';
import { auth } from '../firebaseConfig';
import { CustomerTopNav } from './CustomerTopNav';
import ProcurementShipmentListPage from './ProcurementShipmentListPage';
import ProcurementInvoiceViewerPage from './ProcurementInvoiceViewerPage';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import DescriptionIcon from '@mui/icons-material/Description';
import HomeIcon from '@mui/icons-material/Home';

interface CustomerInfo {
  name: string;
  logoPath: string;
  color: string;
  backgroundColor: string;
  lightColor: string;
}

interface Props {
  customerInfo: CustomerInfo;
}

const SCREEN_CONFIG = [
  { id: 'home', label: '홈' },
  { id: 'procurement', label: '출고 조회' },
  { id: 'invoice', label: '송장' },
];

const CustomerMenuCard = ({
  title,
  icon,
  onClick,
  customerColor,
}: {
  title: string;
  icon: React.ReactNode;
  onClick: () => void;
  customerColor: string;
}) => (
  <Box
    onClick={onClick}
    sx={{
      height: 120,
      cursor: 'pointer',
      background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
      border: `2px solid ${customerColor}20`,
      borderRadius: 3,
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: `0 8px 32px ${customerColor}30`,
        borderColor: `${customerColor}40`,
      },
    }}
  >
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', p: 2 }}>
      <Box sx={{ color: customerColor, mb: 1 }}>{icon}</Box>
      <Typography variant="h6" fontWeight={700} sx={{ color: '#2c3e50', fontSize: { xs: 16, sm: 18 } }}>
        {title}
      </Typography>
    </Box>
  </Box>
);

const CustomerProcurementPage: React.FC<Props> = ({ customerInfo }) => {
  const [currentScreenIndex, setCurrentScreenIndex] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleLogout = async () => {
    try {
      localStorage.removeItem('userConfig');
      await signOut(auth);
      window.location.href = '/';
    } catch (error) {
      console.error('로그아웃 오류:', error);
    }
  };

  const HomeScreen = () => (
    <Box sx={{
      minHeight: '100%',
      background: isMobile
        ? `linear-gradient(135deg, ${customerInfo.backgroundColor} 0%, ${customerInfo.lightColor} 100%)`
        : 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
      py: 4,
      px: 2,
    }}>
      <Box sx={{
        mb: 4,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        bgcolor: isMobile ? 'transparent' : customerInfo.backgroundColor,
        borderRadius: isMobile ? 0 : 4,
        p: isMobile ? 2 : 4,
        mx: 'auto',
        maxWidth: 530,
        boxShadow: isMobile ? 'none' : '0 8px 32px rgba(0,0,0,0.1)',
      }}>
        <img
          src={customerInfo.logoPath}
          alt={customerInfo.name}
          style={{ width: '100%', maxWidth: isMobile ? 120 : 140, height: isMobile ? 60 : 70, objectFit: 'contain', marginBottom: 12 }}
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
        <Typography variant="h4" fontWeight={700} sx={{ color: customerInfo.color, mb: 0.5, textAlign: 'center', fontSize: { xs: 24, sm: 32 } }}>
          {customerInfo.name}
        </Typography>
        <Typography variant="body1" sx={{ color: isMobile ? customerInfo.color : '#666', textAlign: 'center' }}>
          ( 성우로지스 )
        </Typography>
      </Box>

      {!isMobile && (
        <Box sx={{ maxWidth: 480, mx: 'auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
          <CustomerMenuCard
            title="출고 조회"
            icon={<LocalShippingIcon sx={{ fontSize: 36 }} />}
            onClick={() => setCurrentScreenIndex(1)}
            customerColor={customerInfo.color}
          />
          <CustomerMenuCard
            title="송장"
            icon={<DescriptionIcon sx={{ fontSize: 36 }} />}
            onClick={() => setCurrentScreenIndex(2)}
            customerColor={customerInfo.color}
          />
        </Box>
      )}

      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Button variant="outlined" onClick={handleLogout} sx={{ borderColor: '#dee2e6', color: '#6c757d', fontWeight: 600 }}>
          로그아웃
        </Button>
      </Box>
    </Box>
  );

  const renderContent = () => {
    if (currentScreenIndex === 0) return <HomeScreen />;
    if (currentScreenIndex === 1) {
      return (
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', p: { xs: 1, sm: 2 } }}>
          <ProcurementShipmentListPage customerMode />
        </Box>
      );
    }
    return (
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: { xs: 1, sm: 2 } }}>
        <ProcurementInvoiceViewerPage />
      </Box>
    );
  };

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', bgcolor: '#ffffff' }}>
      {currentScreenIndex !== 0 && (
        <CustomerTopNav
          customerInfo={customerInfo}
          screenConfig={SCREEN_CONFIG}
          currentScreenIndex={currentScreenIndex}
          onNavigateHome={() => setCurrentScreenIndex(0)}
          onNavigateScreen={setCurrentScreenIndex}
        />
      )}

      <Box sx={{
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        pb: isMobile && currentScreenIndex !== 0 ? '70px' : 0,
      }}>
        {renderContent()}
      </Box>

      {isMobile && (
        <Box sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          bgcolor: 'white',
          borderTop: '1px solid #e0e0e0',
          boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
          zIndex: 9999,
          display: 'flex',
          justifyContent: 'space-around',
          alignItems: 'center',
          height: 70,
        }}>
          {SCREEN_CONFIG.map((screen, index) => (
            <Box
              key={screen.id}
              onClick={() => setCurrentScreenIndex(index)}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                flex: 1,
                py: 1,
                cursor: 'pointer',
                color: currentScreenIndex === index ? customerInfo.color : '#999',
              }}
            >
              <Box sx={{ fontSize: 24, mb: 0.5 }}>
                {screen.id === 'home' && <HomeIcon />}
                {screen.id === 'procurement' && <LocalShippingIcon />}
                {screen.id === 'invoice' && <DescriptionIcon />}
              </Box>
              <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: currentScreenIndex === index ? 600 : 400 }}>
                {screen.label}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default CustomerProcurementPage;
